//Parcel: npx parcel index.html
//https://docs.amplify.aws/lib/auth/start/q/platform/js/#re-use-existing-authentication-resource
//https://www.npmjs.com/package/amazon-cognito-identity-js
//Reset temporary password:
//https://gjclogin.auth.us-east-2.amazoncognito.com/login?response_type=token&client_id=2pibut80k32ggffm4gjr2aplao&redirect_uri=https://ph.al/
//https://cognito-idp.us-east-2.amazonaws.com/us-east-2_hvEbl5r3G/.well-known/jwks.json
//https://jwt.io/#libraries-io
//https://github.com/maximivanov/cognito-jwt-verifier (LAMBDA JWT VERIFIER)
'use strict';

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import { COGNITO, STORAGE, TOKEN } from './constants.js';

const poolData = {
  UserPoolId: COGNITO.USER_POOL_ID,
  ClientId: COGNITO.CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);
let authenticatedUser = null;
let username = null;

//cache store for configured time
const storeJwtToken = function (
  token,
  key = STORAGE.JWT_TOKEN,
  expirationTimeMs = TOKEN.EXPIRATION_TIME_MS,
) {
  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value: token,
    expiration: expirationTimeMs ? Date.now() + expirationTimeMs : null,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

const getStoredJwtToken = async function (key = STORAGE.JWT_TOKEN) {
  try {
    const item = localStorage.getItem(key);
    // if the item doesn't exist, return null
    if (!item) return null;

    const parsedItem = JSON.parse(item);

    // compare the expiry time of the item with the current time
    if (parsedItem.expiration && Date.now() > parsedItem.expiration) {
      // If the item is expired, try to refresh it
      console.log('Token expired, attempting refresh...');
      return await refreshSession();
    }

    return parsedItem.value;
  } catch (error) {
    console.error('Error getting stored JWT token:', error);
    return null;
  }
};

let jwtToken = null;
// Initialize token
(async () => {
  jwtToken = await getStoredJwtToken(); //if token is not null - show logged in user
})();

// Export a function to get the current token value
function getCurrentToken() {
  return jwtToken;
}

// Export a function to get the initialized token
async function getInitializedToken() {
  // Wait a tick to ensure async initialization has a chance to run
  await new Promise(resolve => setTimeout(resolve, 0));
  return jwtToken;
}

async function refreshSession() {
  // If we don't have an in-memory authenticatedUser, try to recreate it
  if (!authenticatedUser) {
    authenticatedUser = getCognitoUserFromStorage();
    if (!authenticatedUser) {
      console.log('No authenticated user found for refresh');
      return null;
    }
  }

  return new Promise((resolve, reject) => {
    try {
      authenticatedUser.refreshSession(
        authenticatedUser.getSignInUserSession().getRefreshToken(),
        {
          onSuccess: function (result) {
            console.log('Token refresh successful');
            const newToken = result.getIdToken().getJwtToken();
            storeJwtToken(newToken);
            jwtToken = newToken;
            resolve(newToken);
          },
          onFailure: function (err) {
            console.error('Token refresh failed:', err);
            localStorage.removeItem(STORAGE.JWT_TOKEN);
            jwtToken = null;
            authenticatedUser = null;
            resolve(null);
          },
        },
      );
    } catch (err) {
      console.error('refreshSession exception', err);
      resolve(null);
    }
  });
}

function getCognitoUserFromStorage() {
  try {
    const ud = localStorage.getItem(STORAGE.USER_DATA);
    if (!ud) return null;
    const parsed = JSON.parse(ud);
    if (!parsed || !parsed.username) return null;
    return new CognitoUser({ Username: parsed.username, Pool: userPool });
  } catch (e) {
    console.error('Error reconstructing CognitoUser from storage', e);
    return null;
  }
}

/**
 * 		public authenticateUser(
			authenticationDetails: AuthenticationDetails,
			callbacks: IAuthenticationCallback
		): void;
 */
async function cognitoSignIn(inputEmail, inputPassword, localCallback) {
  try {
    const authenticationData = {
      Username: inputEmail,
      Password: inputPassword,
    };

    const userData = {
      Username: inputEmail,
      Pool: userPool,
    };

    // Store user data for session recovery
    localStorage.setItem(
      'gjcUserData',
      JSON.stringify({
        username: inputEmail,
      }),
    );

    const authenticationDetails = new AuthenticationDetails(authenticationData);
    const cognitoUser = new CognitoUser(userData);

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: function (result) {
        //Store authenticated user. Need it to sign out later.
        authenticatedUser = cognitoUser;
        username = inputEmail;
        jwtToken = result.getIdToken().getJwtToken();
        storeJwtToken(jwtToken);

        //call the success function from the passed in object
        localCallback.success(username);
      },

      onFailure: function (err) {
        //call the error function from the passed in object
        localCallback.error(err);
        console.error('error signing in', err);
      },
    });
  } catch (error) {
    //call the error function from the passed in object
    localCallback.error(error);
    console.error('Catch: error signing in', error);
  }
}

/**
 * 		public globalSignOut(callbacks: {
			onSuccess: (msg: string) => void;
			onFailure: (err: Error) => void;
		}): void;
 */
async function cognitoSignOut(localCallback) {
  if (!authenticatedUser) {
    console.error('User is already null, cannot sign out.');
    return;
  }

  authenticatedUser.globalSignOut({
    onSuccess: function (msg) {
      console.log('globalSignOut success:', msg);
      //Clear stored authenticated user and related data
      authenticatedUser = null;
      username = null;
      jwtToken = null;
      localStorage.removeItem(STORAGE.JWT_TOKEN);
      localStorage.removeItem(STORAGE.USER_DATA);

      if (localCallback && typeof localCallback.success === 'function') {
        localCallback.success();
      }
    },

    onFailure: function (err) {
      console.error('error signing out', err);
      if (localCallback && typeof localCallback.error === 'function') {
        localCallback.error(`error signing out ${err}`);
      }
    },
  });
}

// expose refreshSession globally so other modules (callGjcApi) can call it
window.refreshSession = refreshSession;

export {
  cognitoSignIn,
  cognitoSignOut,
  jwtToken,
  storeJwtToken,
  getInitializedToken,
  getCurrentToken,
};
